// MapEditorDemoPage.jsx — internal, dev-only harness for demoing MapEditorModal's multidate /
// mil-symbol capabilities against three hand-authored OSINT-style scenarios (src/dev/mapDemo).
//
// Not part of any production route tree — wired directly in Shell.jsx ahead of the auth gate
// (see .scratch/map-editor-demo/PLAN.md) purely so it's reachable at /dev/map-demo without
// needing a seeded backend account. Bypasses the real Yjs collaboration plumbing entirely:
// `editorState` is built once per scenario via `mapStateFromJSON` (same helper the map schema's
// own test suite uses — see schema/mapSchema.js) and held in plain local state, exactly like
// MapEditorPortal does but without the fragment sync — MapEditorModal itself has no direct Yjs
// dependency, only its portal does.

import React, { useCallback, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import PublicIcon from '@mui/icons-material/Public';

import MapEditorModal from '../../components/map/editor/MapEditorModal';
import { mapStateFromJSON, DEFAULT_BASEMAP, OVERLAY_TYPES } from '../../components/map/schema/mapSchema';
import { SCENARIOS, getScenario } from './scenarios';

const DEMO_USER = {
    uid: 'demo-user-1',
    id: 'demo-user-1',
    email: 'demo@dmetrics.internal',
    displayName: 'Demo Analyst',
    role: 'analyst',
    roles: ['analyst'],
};

function formatDate(ms) {
    if (!ms) return '—';
    return new Date(ms).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

const ScenarioPicker = ({ onSelect }) => (
    <Stack sx={{ height: '100vh', overflowY: 'auto', bgcolor: 'background.default', p: 4, alignItems: 'center' }}>
        <Stack spacing={1} alignItems="center" sx={{ mb: 4, maxWidth: 720, textAlign: 'center' }}>
            <PublicIcon sx={{ fontSize: 40, color: 'primary.main' }} />
            <Typography variant="h4" fontWeight={700}>MapEditorModal — Multidate Demo</Typography>
            <Typography color="text.secondary">
                Three hand-authored OSINT-style scenarios, each with mil-symbol markings whose position/status
                changes over time. Pick one, then drag the timeline at the bottom of the map to scrub through it.
            </Typography>
            <Typography variant="caption" color="text.disabled">
                Illustrative / approximate — for demonstration only, not intelligence-grade sourcing.
            </Typography>
        </Stack>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} sx={{ width: '100%', maxWidth: 1100 }}>
            {SCENARIOS.map(({ meta }) => (
                <Card key={meta.id} sx={{ flex: 1 }} variant="outlined">
                    <CardActionArea onClick={() => onSelect(meta.id)} sx={{ height: '100%' }}>
                        <CardContent>
                            <Typography variant="h6" fontWeight={600} gutterBottom>{meta.title}</Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, minHeight: 66 }}>
                                {meta.summary}
                            </Typography>
                            <Chip size="small" label={`${formatDate(meta.dtgStartMs)} → ${formatDate(meta.dtgEndMs)}`} />
                        </CardContent>
                    </CardActionArea>
                </Card>
            ))}
        </Stack>
    </Stack>
);

const MapEditorDemoPage = () => {
    const [scenarioId, setScenarioId] = useState(null);
    const scenario = scenarioId ? getScenario(scenarioId) : null;

    const [editorState, setEditorState] = useState(null);
    const remoteChangeRef = useRef(false);

    const handleSelect = useCallback((id) => {
        const { doc } = getScenario(id);
        setEditorState(mapStateFromJSON(doc));
        setScenarioId(id);
    }, []);

    const handleBack = useCallback(() => {
        setScenarioId(null);
        setEditorState(null);
    }, []);

    // No-ops: this harness doesn't persist anything back anywhere (see file header).
    const noop = useCallback(() => {}, []);

    const initialDtgSelectedMs = useMemo(() => scenario?.meta.initialDtgMs ?? null, [scenario]);

    if (!scenario || !editorState) {
        return <ScenarioPicker onSelect={handleSelect} />;
    }

    return (
        <Box sx={{ position: 'relative', height: '100vh', width: '100%' }}>
            <MapEditorModal
                key={scenarioId}
                viewId={null}
                dtgSelectedMs={initialDtgSelectedMs}
                onDtgRangeChange={noop}
                title={scenario.meta.title}
                basemapStyleId={DEFAULT_BASEMAP}
                activeGridId={null}
                overlayType={OVERLAY_TYPES.MAPBOX}
                onWindowSettingsChange={noop}
                editorState={editorState}
                setEditorState={setEditorState}
                onClose={handleBack}
                currentUser={DEMO_USER}
                remoteChangeRef={remoteChangeRef}
                isReadOnly={false}
            />
        </Box>
    );
};

export default MapEditorDemoPage;
